import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faFire, faSnowflake, faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { GameState, Player } from '../../../domain/entities';
import { View } from '../../../App';
import { soundService } from '../../../services/SoundService';
import { PlayerFace } from '../../shared/PlayerFace';
import { usePanelViewPreferences } from '../../../hooks/usePanelViewPreferences';

interface TeamStatusPanelProps {
    gameState: GameState;
    pushView: (view: View) => void;
}

export const TeamStatusPanel: React.FC<TeamStatusPanelProps> = ({ gameState, pushView }) => {
    const { isCompact, toggleView } = usePanelViewPreferences();
    const playerTeam = useMemo(() => gameState.leagues[gameState.playerLeagueName]?.teams.find(t => t.name === gameState.playerTeamName), [gameState]);

    const teamStatus = useMemo(() => {
        if (!playerTeam) return {
            inForm: [],
            outOfForm: []
        };

        const inForm = playerTeam.squad.filter(p => p.form >= 2).sort((a,b) => b.form - a.form);
        const outOfForm = playerTeam.squad.filter(p => p.form <= -2).sort((a,b) => a.form - b.form);

        return {
            inForm,
            outOfForm
        };
    }, [playerTeam]);

    const handlePlayerClick = (e: React.MouseEvent, player: Player) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const isCompactView = isCompact('teamStatus');

    return (
        <div className="panel dashboard-team-status">
            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faChartBar} />
                    Player Form
                </div>
                <button
                    onClick={() => toggleView('teamStatus')}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        opacity: 0.7,
                        transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                    title={isCompactView ? 'Show full view' : 'Show compact view'}
                >
                    <FontAwesomeIcon icon={isCompactView ? faExpand : faCompress} />
                </button>
            </div>
            <div className="panel-content">
                {isCompactView ? (
                    // Compact View - Show only top player from each category
                    <div className="overview-section">
                        {teamStatus.inForm.length > 0 && (
                            <div className="overview-form-group">
                                <span className="overview-form-label"><FontAwesomeIcon icon={faFire} style={{ marginRight: '4px', color: 'var(--negative-color)' }} />Hot Streak</span>
                                <ul className="overview-form-list">
                                    {teamStatus.inForm.slice(0, 1).map(p => (
                                        <li key={p.id}>
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, p)}>
                                                <PlayerFace player={p} teamColor={playerTeam?.colors.primary} />{p.name}
                                            </button>
                                            <span className="positive">+{p.form}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {teamStatus.outOfForm.length > 0 && (
                            <div className="overview-form-group">
                                <span className="overview-form-label"><FontAwesomeIcon icon={faSnowflake} style={{ marginRight: '4px', color: 'var(--info-color)' }} />Cold Streak</span>
                                <ul className="overview-form-list">
                                    {teamStatus.outOfForm.slice(0, 1).map(p => (
                                        <li key={p.id}>
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, p)}>
                                                <PlayerFace player={p} teamColor={playerTeam?.colors.primary} />{p.name}
                                            </button>
                                            <span className="negative">{p.form}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {teamStatus.inForm.length === 0 && teamStatus.outOfForm.length === 0 && (
                            <div className="overview-all-clear">
                                <span>✅ Squad form is stable!</span>
                            </div>
                        )}
                    </div>
                ) : (
                    // Full View
                    <>
                {/* Form Section */}
                {(teamStatus.inForm.length > 0 || teamStatus.outOfForm.length > 0) ? (
                    <div className="overview-section">
                        {teamStatus.inForm.length > 0 && (
                            <div className="overview-form-group">
                                <span className="overview-form-label"><FontAwesomeIcon icon={faFire} style={{ marginRight: '4px', color: 'var(--negative-color)' }} />Hot Streak</span>
                                <ul className="overview-form-list">
                                    {teamStatus.inForm.slice(0, 3).map(p => (
                                        <li key={p.id}>
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, p)}>
                                                <PlayerFace player={p} teamColor={playerTeam?.colors.primary} />{p.name}
                                            </button>
                                            <span className="positive">+{p.form}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {teamStatus.outOfForm.length > 0 && (
                            <div className="overview-form-group">
                                <span className="overview-form-label"><FontAwesomeIcon icon={faSnowflake} style={{ marginRight: '4px', color: 'var(--info-color)' }} />Cold Streak</span>
                                <ul className="overview-form-list">
                                    {teamStatus.outOfForm.slice(0, 3).map(p => (
                                        <li key={p.id}>
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, p)}>
                                                <PlayerFace player={p} teamColor={playerTeam?.colors.primary} />{p.name}
                                            </button>
                                            <span className="negative">{p.form}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="overview-all-clear">
                        <span>✅ Squad form is stable!</span>
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    );
};
